import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import DashboardLayout from '../Layout/DashboardLayout';
import { toast } from 'react-toastify';
import './Admin.css';

const AdminFeatures = () => {
    const { getUsers, updateUserFeatures } = useAdmin();
    const [globalFeatures, setGlobalFeatures] = useState({
        registrationsOpen: true,
        maintenanceMode: false,
        googleLoginEnabled: true,
        facebookLoginEnabled: false,
        autoApproveTemplates: false
    });

    const [defaultLimits, setDefaultLimits] = useState({
        maxContacts: 1000,
        maxCampaigns: 5,
        maxFlows: 3,
        dailyMessageLimit: 250
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userFeatures, setUserFeatures] = useState([]);
    const [userFlowLimit, setUserFlowLimit] = useState(5);
    const [isUpdating, setIsUpdating] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchTerm.trim()) return;
        try {
            const response = await getUsers({ search: searchTerm, limit: 5 });
            setSearchResults(response.users || []);
        } catch (err) {
            console.error('Search failed:', err);
        }
    };

    const selectUser = (user) => {
        setSelectedUser(user);
        setUserFeatures(user.enabledFeatures || []);
        setUserFlowLimit(user.flowLimit || 5);
        setSearchResults([]);
        setSearchTerm('');
    };

    const handleGlobalToggle = (feature) => {
        setGlobalFeatures({
            ...globalFeatures,
            [feature]: !globalFeatures[feature]
        });
    };

    const handleUserFeatureToggle = (feature) => {
        if (userFeatures.includes(feature)) {
            setUserFeatures(userFeatures.filter(f => f !== feature));
        } else {
            setUserFeatures([...userFeatures, feature]);
        }
    };

    const saveUserChanges = async () => {
        if (!selectedUser) return;
        setIsUpdating(true);
        try {
            const response = await updateUserFeatures(selectedUser._id, userFeatures, userFlowLimit);
            if (response.success) {
                toast.success(`Features updated for ${selectedUser.name}`);
                setSelectedUser({ ...selectedUser, enabledFeatures: userFeatures, flowLimit: userFlowLimit });
            }
        } catch (err) {
            toast.error('Failed to update user features');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <DashboardLayout isAdmin={true}>
            <div className="admin-page">
                <header className="admin-header">
                    <h1>Feature Management</h1>
                </header>

                <div className="admin-section">
                    <h2>Individual User Management</h2>
                    <div className="search-container mb-4 position-relative">
                        <form onSubmit={handleSearch} className="d-flex gap-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search user to manage features..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <button type="submit" className="btn btn--primary">Search</button>
                        </form>
                        {searchResults.length > 0 && (
                            <div className="search-results-dropdown shadow">
                                {searchResults.map(u => (
                                    <div key={u._id} className="search-result-item" onClick={() => selectUser(u)}>
                                        {u.name} ({u.email})
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedUser && (
                        <div className="user-feature-editor animate-fade-in">
                            <div className="p-3 bg-secondary rounded mb-4 d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="mb-1">{selectedUser.name}</h4>
                                    <p className="mb-0 text-muted">{selectedUser.email}</p>
                                </div>
                                <button className="btn btn--outline btn--sm" onClick={() => setSelectedUser(null)}>Clear</button>
                            </div>

                            <div className="admin-card-grid">
                                {['campaigns', 'analytics', 'contacts', 'templates', 'reports', 'automation'].map(feature => (
                                    <div key={feature} className="feature-item">
                                        <div className="feature-info">
                                            <h4>{feature.charAt(0).toUpperCase() + feature.slice(1)}</h4>
                                            <p>Access to {feature} module</p>
                                        </div>
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={userFeatures.includes(feature)}
                                                onChange={() => handleUserFeatureToggle(feature)}
                                            />
                                            <span className="slider"></span>
                                        </label>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 p-4 border rounded">
                                <div className="form-group mb-0" style={{ maxWidth: '400px' }}>
                                    <label className="form-label">Flow Limit</label>
                                    <div className="d-flex gap-2">
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={userFlowLimit}
                                            onChange={(e) => setUserFlowLimit(parseInt(e.target.value))}
                                        />
                                        <button
                                            className="btn btn--primary"
                                            onClick={saveUserChanges}
                                            disabled={isUpdating}
                                        >
                                            {isUpdating ? 'Saving...' : 'Save All Changes'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="admin-section">
                    <h2>Global System Toggles</h2>
                    <div className="admin-card-grid">
                        <div className="feature-item">
                            <div className="feature-info">
                                <h4>New Registrations</h4>
                                <p>Allow new users to create accounts</p>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={globalFeatures.registrationsOpen}
                                    onChange={() => handleGlobalToggle('registrationsOpen')}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>

                        <div className="feature-item">
                            <div className="feature-info">
                                <h4>Maintenance Mode</h4>
                                <p>Redirect all users to maintenance page</p>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={globalFeatures.maintenanceMode}
                                    onChange={() => handleGlobalToggle('maintenanceMode')}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>

                        <div className="feature-item">
                            <div className="feature-info">
                                <h4>Google Login</h4>
                                <p>Enable OAuth login via Google</p>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={globalFeatures.googleLoginEnabled}
                                    onChange={() => handleGlobalToggle('googleLoginEnabled')}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>

                        <div className="feature-item">
                            <div className="feature-info">
                                <h4>Auto-Approve Templates</h4>
                                <p>Automatically approve local templates (non-Meta)</p>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={globalFeatures.autoApproveTemplates}
                                    onChange={() => handleGlobalToggle('autoApproveTemplates')}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="admin-section">
                    <h2>Default New User Limits</h2>
                    <div className="admin-form-grid">
                        <div className="form-group">
                            <label className="form-label">Default Max Contacts</label>
                            <input
                                type="number"
                                className="form-control"
                                value={defaultLimits.maxContacts}
                                onChange={(e) => setDefaultLimits({ ...defaultLimits, maxContacts: parseInt(e.target.value) })}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Default Max Campaigns</label>
                            <input
                                type="number"
                                className="form-control"
                                value={defaultLimits.maxCampaigns}
                                onChange={(e) => setDefaultLimits({ ...defaultLimits, maxCampaigns: parseInt(e.target.value) })}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Default Max Flows</label>
                            <input
                                type="number"
                                className="form-control"
                                value={defaultLimits.maxFlows}
                                onChange={(e) => setDefaultLimits({ ...defaultLimits, maxFlows: parseInt(e.target.value) })}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Daily Message Limit</label>
                            <input
                                type="number"
                                className="form-control"
                                value={defaultLimits.dailyMessageLimit}
                                onChange={(e) => setDefaultLimits({ ...defaultLimits, dailyMessageLimit: parseInt(e.target.value) })}
                            />
                        </div>
                    </div>
                    <button className="btn btn--primary mt-8">Save Default Limits</button>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AdminFeatures;
